import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useGetBankDetailsHistoryQuery } from '../services/bankApi';
import { AggregatorOption, BankRow } from '../types/bankdetails';

interface BankHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  selectedBank: BankRow | null;
  aggregator: AggregatorOption;
}

export function BankHistoryDialog({
  open,
  onClose,
  selectedBank,
  aggregator,
}: BankHistoryDialogProps) {
  const {
    data: historyData,
    isLoading,
    error,
  } = useGetBankDetailsHistoryQuery(
    {
      ifsc: selectedBank?.ifscCode,
    },
    {
      skip: !selectedBank,
    }
  );

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Bank Details History</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <Typography>Loading history...</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Bank Details History</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <Typography color="error">Failed to load history</Typography>
            <Typography variant="body2" color="text.secondary">
              Error: {JSON.stringify(error)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const historyItems = historyData?.data?.data || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Bank Details History</DialogTitle>
      <DialogContent>
        {historyItems.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography>No history found for this bank</Typography>
            <Typography variant="body2" color="text.secondary">
              IFSC: {selectedBank?.ifscCode}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 1 }}>
            <TableContainer component={Paper} sx={{ borderRadius: '8px' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Transaction Type</TableCell>
                    <TableCell>Bank ID</TableCell>
                    <TableCell>Updated Date</TableCell>
                    <TableCell>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyItems.map((item, index) => (
                    <TableRow key={item.id || index} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.transactionType}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.bankId}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(item.updatedDate).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.remarks || '-'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
